import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { User } from "lucide-react";
import { api } from "@/lib/api";
import TeamCrest from "@/components/TeamCrest";
import FacebookShare from "@/components/FacebookShare";

const HEADERS = ["RK", "Team", "GP", "W", "L", "PCT", "PF", "PA", "DIFF", "STRK"];

export default function Standings() {
    const [rows, setRows] = useState(null);
    const [leaders, setLeaders] = useState(null);

    useEffect(() => {
        api.get("/standings").then((r) => setRows(r.data)).catch(() => setRows([]));
        api.get("/leaders").then((r) => setLeaders(r.data)).catch(() => {});
    }, []);

    const fmtPct = (r) => (r.gp ? r.pct.toFixed(3).replace(/^0/, "") : ".000");

    return (
        <div data-testid="standings-page" className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#FF5500] mb-3">The Ladder</p>
            <div className="flex items-end justify-between gap-4 flex-wrap">
                <h1 className="font-display font-black uppercase text-4xl sm:text-5xl text-white">Standings</h1>
                <FacebookShare
                    path="/share/standings"
                    target="standings-capture"
                    fileName="ykpbl-standings.png"
                    label="Share Standings"
                    testid="share-standings"
                />
            </div>
            <p className="mt-3 text-slate-400 text-sm max-w-lg">
                Auto-computed from every final result. Win percentage rules the board.
            </p>

            {rows === null ? (
                <p className="mt-16 text-slate-500 font-stat text-sm">Loading standings…</p>
            ) : rows.length === 0 ? (
                <div data-testid="standings-empty" className="mt-12 card-arena rounded-2xl p-12 text-center">
                    <p className="font-display font-bold uppercase text-xl text-slate-300">No teams registered yet</p>
                </div>
            ) : (
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="mt-12 card-arena rounded-2xl overflow-hidden"
                    data-testid="standings-capture"
                >
                    <div className="overflow-x-auto">
                        <table data-testid="standings-table" className="w-full text-sm min-w-[720px]">
                            <thead>
                                <tr className="bg-[#1A1A28] text-left">
                                    {HEADERS.map((h) => (
                                        <th
                                            key={h}
                                            className={`px-4 py-4 text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500 ${
                                                h !== "Team" ? "text-center" : ""
                                            }`}
                                        >
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => {
                                    const top = r.rank <= 3;
                                    return (
                                        <tr
                                            key={r.team_id}
                                            data-testid={`standings-row-${r.rank}`}
                                            className="border-t border-[#262638] hover:bg-[#1A1A28]/60 transition-colors duration-200"
                                        >
                                            <td className="px-4 py-4">
                                                <span
                                                    className={`font-stat font-bold text-sm w-8 h-8 inline-flex items-center justify-center rounded-lg ${
                                                        r.rank === 1
                                                            ? "bg-[#FFB800]/15 text-[#FFB800] border border-[#FFB800]/50"
                                                            : top
                                                              ? "bg-[#FF5500]/10 text-[#FF5500] border border-[#FF5500]/40"
                                                              : "text-slate-400"
                                                    }`}
                                                >
                                                    {r.rank}
                                                </span>
                                            </td>
                                            <td className="px-4 py-4">
                                                <div className="flex items-center gap-3">
                                                    <TeamCrest team={r} size="sm" />
                                                    <div>
                                                        <p className="font-display font-bold uppercase text-white leading-none">
                                                            {r.name}
                                                        </p>
                                                        <p className="text-[10px] text-slate-500 uppercase tracking-widest mt-0.5">
                                                            {r.city}
                                                        </p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-4 py-4 text-center font-stat text-slate-300">{r.gp}</td>
                                            <td className="px-4 py-4 text-center font-stat font-bold text-[#22C55E]">{r.w}</td>
                                            <td className="px-4 py-4 text-center font-stat font-bold text-[#EF4444]">{r.l}</td>
                                            <td className="px-4 py-4 text-center font-stat font-bold text-white">{fmtPct(r)}</td>
                                            <td className="px-4 py-4 text-center font-stat text-slate-300">{r.pf}</td>
                                            <td className="px-4 py-4 text-center font-stat text-slate-300">{r.pa}</td>
                                            <td
                                                className={`px-4 py-4 text-center font-stat font-bold ${
                                                    r.diff > 0 ? "text-[#22C55E]" : r.diff < 0 ? "text-[#EF4444]" : "text-slate-400"
                                                }`}
                                            >
                                                {r.diff > 0 ? `+${r.diff}` : r.diff}
                                            </td>
                                            <td className="px-4 py-4 text-center">
                                                <span
                                                    className={`font-stat font-bold text-xs px-2 py-1 rounded ${
                                                        r.streak.startsWith("W")
                                                            ? "text-[#22C55E] bg-[#22C55E]/10"
                                                            : r.streak.startsWith("L")
                                                              ? "text-[#EF4444] bg-[#EF4444]/10"
                                                              : "text-slate-500"
                                                    }`}
                                                >
                                                    {r.streak}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </motion.div>
            )}

            {leaders && (
                <div data-testid="league-leaders" className="mt-16">
                    <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#FFB800] mb-3">
                        Season Leaders
                    </p>
                    <h2 className="font-display font-black uppercase text-3xl sm:text-4xl text-white">
                        League Leaders
                    </h2>
                    <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                        {[
                            ["ppg", "Points"],
                            ["apg", "Assists"],
                            ["rpg", "Rebounds"],
                            ["spg", "Steals"],
                            ["blk", "Blocks"],
                        ].map(([key, label]) => (
                            <div key={key} data-testid={`leaders-${key}`} className="card-arena rounded-2xl overflow-hidden">
                                <div className="px-4 py-3 bg-[#1A1A28] flex items-center justify-between">
                                    <span className="font-display font-bold uppercase tracking-widest text-sm text-white">
                                        {label}
                                    </span>
                                    <span className="font-stat text-[10px] text-[#FFB800] uppercase">{key}</span>
                                </div>
                                {(leaders[key] || []).length === 0 ? (
                                    <p className="px-4 py-6 text-xs text-slate-500 text-center">
                                        No stats recorded yet
                                    </p>
                                ) : (
                                    leaders[key].map((p) => (
                                        <div
                                            key={p.player_id}
                                            className="flex items-center gap-3 px-4 py-3 border-t border-[#262638]"
                                        >
                                            <span
                                                className={`font-stat font-bold text-xs w-5 ${
                                                    p.rank === 1 ? "text-[#FFB800]" : "text-slate-500"
                                                }`}
                                            >
                                                {p.rank}
                                            </span>
                                            {p.photo_url ? (
                                                <img
                                                    src={p.photo_url}
                                                    alt={p.name}
                                                    className="w-9 h-9 rounded-full object-cover object-top border border-[#262638]"
                                                />
                                            ) : (
                                                <div className="w-9 h-9 rounded-full bg-[#1A1A28] border border-[#262638] flex items-center justify-center">
                                                    <User className="w-4 h-4 text-slate-600" />
                                                </div>
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <p className="font-display font-bold uppercase text-sm text-white truncate">
                                                    {p.name}
                                                </p>
                                                <p className="text-[10px] text-slate-500 uppercase tracking-widest flex items-center gap-1.5">
                                                    {p.team?.logo && (
                                                        <img
                                                            src={p.team.logo}
                                                            alt={p.team.short_name || p.team.name || "Team logo"}
                                                            className="w-4 h-4 object-contain"
                                                        />
                                                    )}
                                                    {p.team?.short_name} • {p.position}
                                                </p>
                                            </div>
                                            <span className="font-stat font-bold text-lg text-[#FF5500]">{p.value}</span>
                                        </div>
                                    ))
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
